import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { cwTodayISO, cwPrettyDate, nightsBetween, humanCheckedInAt } from '../lib/format';
import { Search, Loader2, ChevronDown, Printer } from 'lucide-react';
import { toast } from 'sonner';
import { useRegisterStatus } from '../context/RegisterStatus';

function addDaysISO(base, n) {
  const [y, m, d] = base.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + n));
  return dt.toISOString().slice(0, 10);
}

export default function Arrivals() {
  const [bookings, setBookings] = useState(null);
  const [q, setQ] = useState('');
  const [checkingIn, setCheckingIn] = useState(null);
  const [showArrived, setShowArrived] = useState(false);
  const { isStale } = useRegisterStatus() || {};
  const nav = useNavigate();

  const load = useCallback(async () => {
    const { data } = await api.get('/bookings');
    setBookings(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const today = cwTodayISO();

  const { groups, arrivedToday } = useMemo(() => {
    if (!bookings) return { groups: [], arrivedToday: [] };
    const confirmed = bookings.filter(b => b.Status === 'Confirmed');
    const query = q.trim().toLowerCase();
    const filtered = query
      ? confirmed.filter(b => b.Name.toLowerCase().includes(query) || b['Booking#'].toLowerCase().includes(query))
      : confirmed;

    const arrivedToday = filtered.filter(b => b.CheckedInAt && b.CheckIn === today);

    const groups = [];
    for (let i = 0; i <= 6; i++) {
      const dateStr = addDaysISO(today, i);
      const rows = filtered.filter(b => b.CheckIn === dateStr && !b.CheckedInAt);
      const label = i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : cwPrettyDate(dateStr);
      groups.push({ label, dateStr, rows });
    }
    return { groups, arrivedToday };
  }, [bookings, q, today]);

  const doCheckIn = async (bn) => {
    setCheckingIn(bn);
    try {
      await api.post('/bookings/check-in', { booking_number: bn });
      toast.success('Guest checked in');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not check in');
    } finally { setCheckingIn(null); }
  };

  if (!bookings) return <div className="flex items-center gap-2"><Loader2 className="animate-spin" size={16}/> Loading…</div>;

  return (
    <div>
      <div className="mb-5 flex items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl text-[#1F5A24]">Arrivals</h1>
          <p className="text-[#333]/70 mt-1">Confirmed guests, grouped by date.</p>
        </div>
        <button
          data-testid="arrivals-print"
          onClick={() => nav('/print/arrivals')}
          className="inline-flex items-center gap-2 px-4 py-3 min-h-[3rem] rounded-lg border border-[#CFD8CF] hover:bg-[#E8F5E9] text-[#1F5A24] font-medium"
        >
          <Printer size={16} aria-hidden/> Print today
        </button>
      </div>

      <div className="relative mb-5">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#333]/50" />
        <input
          data-testid="arrivals-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name or booking number"
          className="w-full pl-9 pr-4 py-3 min-h-[3rem] bg-white border border-[#CFD8CF] rounded-lg focus:ring-2 focus:ring-[#2E7D32] outline-none"
        />
      </div>

      <div className="space-y-6">
        {groups.map((g) => (
          <section key={g.dateStr}>
            <div className="sticky top-16 z-10 -mx-4 sm:-mx-0 bg-[#FDF6EC]/90 backdrop-blur px-4 sm:px-0 py-2">
              <h2 className="font-heading text-lg text-[#1F5A24]">
                {g.label} <span className="text-[#333]/60 text-sm">· {g.rows.length} {g.rows.length === 1 ? 'guest' : 'guests'}</span>
              </h2>
            </div>
            {g.rows.length === 0 ? (
              <div className="text-[#333]/60 py-2">Nothing on the schedule.</div>
            ) : (
              <ul className="space-y-3">
                {g.rows.map((b) => (
                  <li key={b['Booking#']} className="bg-[#FBFCFB] border border-[#CFD8CF] rounded-xl p-4 flex items-center gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium truncate">{b.Name}</div>
                      <div className="text-sm text-[#333]/70">
                        {b.RoomType} · {b.Guests} guest{b.Guests>1?'s':''} · {nightsBetween(b.CheckIn, b.CheckOut)} night{nightsBetween(b.CheckIn, b.CheckOut)===1?'':'s'}
                      </div>
                      <div className="text-xs text-[#333]/60 mt-0.5">
                        {cwPrettyDate(b.CheckIn)} → {cwPrettyDate(b.CheckOut)} · {b['Booking#']}
                      </div>
                    </div>
                    <button
                      data-testid={`arrivals-checkin-${b['Booking#']}`}
                      disabled={checkingIn === b['Booking#'] || isStale}
                      onClick={() => doCheckIn(b['Booking#'])}
                      title={isStale ? 'Paused — the register may be out of date' : ''}
                      className="px-5 py-3 rounded-lg bg-[#2E7D32] hover:bg-[#1F5A24] text-white font-medium min-h-[3rem] disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {checkingIn === b['Booking#'] ? '…' : 'Check in'}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        ))}
      </div>

      {arrivedToday.length > 0 && (
        <section className="mt-8">
          <button
            data-testid="arrived-toggle"
            onClick={() => setShowArrived((v) => !v)}
            className="w-full flex items-center gap-2 px-4 py-3 bg-[#E8F5E9] hover:bg-[#C8E6C9] rounded-xl text-[#1F5A24] font-medium"
          >
            <ChevronDown size={16} className={`transition-transform ${showArrived ? 'rotate-180' : ''}`} />
            Arrived today ({arrivedToday.length})
          </button>
          {showArrived && (
            <ul className="mt-3 space-y-2">
              {arrivedToday.map((b) => (
                <li key={b['Booking#']} className="p-3 border border-[#CFD8CF] rounded-lg bg-white flex items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{b.Name}</div>
                    <div className="text-sm text-[#333]/70">
                      {humanCheckedInAt(b.CheckedInAt)} {b.CheckedInBy ? `· by ${b.CheckedInBy}` : ''}
                    </div>
                  </div>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold text-white bg-[#2E7D32]">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    Checked in
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}
